import { qualityAllowed } from "video-quality";

export function holdVideo(connection, reducedMotion) {
  return reducedMotion || connection?.saveData === true || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || "");
}

export async function selectSources(
  sources,
  { mobile, connection, decodingInfo, canPlayType },
) {
  const candidates = (Array.isArray(sources) ? sources : []).filter(
    (source) =>
      source &&
      source.url &&
      source.type &&
      source.width > 0 &&
      source.height > 0 &&
      (source.tier !== "quality" || qualityAllowed(source, connection)),
  );
  const pending = candidates.map(async (source) => {
    if (source.codec === "h264")
      return canPlayType(source.type) ? source : null;
    if (!decodingInfo) return null;
    let timeout;
    try {
      const info = await Promise.race([
        decodingInfo({
          type: "file",
          video: {
            contentType: source.type,
            width: source.width,
            height: source.height,
            bitrate: source.bitrate,
            framerate: source.framerate,
          },
        }),
        new Promise((resolve) => {
          timeout = setTimeout(() => resolve(null), 800);
        }),
      ]);
      return info?.supported && info.smooth && info.powerEfficient
        ? source
        : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  });
  const sourcesReady = Promise.all(pending).then((sources) =>
    sources.filter(Boolean),
  );
  // Assess every codec in parallel, but an accepted preferred codec must not wait
  // for a lower-priority decoder. Recovery can await the remaining results later.
  for (const assessment of pending) {
    const choice = await assessment;
    if (choice) return { choice, sourcesReady };
  }
  return { choice: null, sourcesReady };
}

export class HeroVideo extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.video = this.querySelector("video");
    this.poster = this.querySelector("picture img");
    this.button = document.querySelector(`[data-hero-control="${this.id}"]`);
    if (!this.video || !this.poster) return;
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.motion = matchMedia("(prefers-reduced-motion: reduce)");
    this.mobile = matchMedia("(max-width: 999px)").matches;
    this.connection = navigator.connection;
    this.slide = this.closest(".slideshow__slide");
    this.inView =
      this.getBoundingClientRect().bottom > 0 &&
      this.getBoundingClientRect().top < innerHeight;
    this.userPaused = false;
    this.manual = false;
    this.failed = false;
    this.firstFrame = false;
    this.starting = false;
    this.recovering = false;
    this.buffering = false;
    this.generation = (this.generation || 0) + 1;
    this.retries = 0;
    this.video.muted = true;
    this.button?.setAttribute("disabled", "");
    this.button?.removeAttribute("hidden");
    this.button?.addEventListener("click", () => this.toggle(), { signal });
    this.video.addEventListener(
      "playing",
      () => {
        this.clearStall();
        if (this.video.requestVideoFrameCallback) {
          this.frameCallback = this.video.requestVideoFrameCallback(() =>
            this.reveal(),
          );
        } else if (this.video.readyState >= 2) this.reveal();
      },
      { signal },
    );
    this.video.addEventListener("pause", () => this.syncControl(), { signal });
    this.video.addEventListener("waiting", () => this.watchStall(), { signal });
    this.video.addEventListener("stalled", () => this.watchStall(), { signal });
    this.video.addEventListener("progress", () => {
      if (this.idleTimer) this.watchProgress();
    }, { signal });
    this.video.addEventListener("error", () => this.recover(), { signal });
    this.video.addEventListener(
      "loadedmetadata",
      () => {
        if (this.resumeTime && this.resumeTime < this.video.duration)
          this.video.currentTime = this.resumeTime;
        this.resumeTime = 0;
      },
      { signal },
    );
    document.addEventListener("visibilitychange", () => this.update(), {
      signal,
    });
    const preferenceChanged = () => {
      this.manual = false;
      this.update();
    };
    this.motion.addEventListener("change", preferenceChanged, { signal });
    this.connection?.addEventListener("change", preferenceChanged, { signal });
    this.observer = new IntersectionObserver(
      (entries) => {
        this.inView = entries.some(
          (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.05,
        );
        this.update();
      },
      { threshold: [0, 0.05] },
    );
    this.observer.observe(this);
    if (this.slide) {
      this.slideObserver = new MutationObserver(() => this.update());
      this.slideObserver.observe(this.slide, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
    this.prepare();
  }

  disconnectedCallback() {
    this.generation++;
    this.ready = false;
    this.abort?.abort();
    this.observer?.disconnect();
    this.slideObserver?.disconnect();
    this.unload();
    this.abort = null;
  }

  async prepare() {
    const generation = this.generation;
    let manifest;
    try {
      manifest = JSON.parse(this.dataset.sources);
    } catch {
      manifest = {};
    }
    const variant = this.mobile ? "mobile" : "desktop";
    let sources =
      this.dataset[`${variant}Optimized`] === "true"
        ? manifest?.[variant]
        : null;
    if (!Array.isArray(sources) || !sources.length) {
      try {
        sources = JSON.parse(this.dataset[`${variant}Fallback`]);
      } catch {
        sources = [];
      }
    }
    const selectionOptions = {
      mobile: this.mobile,
      connection: this.connection,
      decodingInfo: navigator.mediaCapabilities?.decodingInfo?.bind(
        navigator.mediaCapabilities,
      ),
      canPlayType: (type) => this.video.canPlayType(type),
    };
    const qualityInitiallyEligible = Array.isArray(sources) && sources.some(
      (source) => source?.tier === "quality" && qualityAllowed(source, this.connection),
    );
    let selected = await selectSources(sources, selectionOptions);
    if (!selected.choice) {
      try {
        selected = await selectSources(
          JSON.parse(this.dataset[`${variant}Fallback`]),
          selectionOptions,
        );
      } catch {}
    }
    if (!this.isConnected || this.generation !== generation) return;
    if (!selected.choice) {
      this.button?.setAttribute("hidden", "");
      return;
    }

    // A stalled image must not strand playback; keep its element until a real
    // video frame arrives even when this startup gate reaches its deadline.
    let posterTimeout;
    try {
      await Promise.race([
        this.poster.decode(),
        new Promise((resolve) => {
          posterTimeout = setTimeout(resolve, 2000);
        }),
      ]);
    } catch {
      /* The film can still play if its poster fails. */
    } finally {
      clearTimeout(posterTimeout);
    }
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    );
    if (!this.isConnected || this.generation !== generation) return;
    // The poster may provide the first usable transfer evidence on browsers
    // without network hints. Reassess before attaching any media URL, never by
    // downloading a second film after playback has started.
    if (!qualityInitiallyEligible && selected.choice.tier !== "quality" && Array.isArray(sources) && sources.some(
      (source) => source?.tier === "quality" && qualityAllowed(source, this.connection),
    )) {
      let upgradeTimeout;
      const upgraded = await Promise.race([
        selectSources(sources, selectionOptions),
        new Promise(resolve => { upgradeTimeout = setTimeout(() => resolve(null), 80); }),
      ]);
      clearTimeout(upgradeTimeout);
      if (!this.isConnected || this.generation !== generation) return;
      if (upgraded?.choice) selected = upgraded;
    }
    this.sourcesReady = selected.sourcesReady;
    this.choice = selected.choice;
    this.ready = true;
    this.button?.removeAttribute("disabled");
    this.emit("poster-ready");
    this.update();
  }

  eligible() {
    return (
      this.isConnected &&
      !document.hidden &&
      this.inView &&
      (!this.slide || this.slide.classList.contains("is-selected"))
    );
  }

  update() {
    if (!this.ready) return;
    if (this.button && this.slide) this.button.hidden = !this.slide.classList.contains("is-selected");
    this.syncControl();
    if (!this.manual && holdVideo(this.connection, this.motion.matches)) {
      this.unload();
      return;
    }
    if (document.hidden) {
      this.suspend();
      return;
    }
    if (!this.eligible()) {
      this.unload();
      return;
    }
    if (!this.userPaused && !this.failed) this.start();
  }

  toggle() {
    if (!this.ready) return;
    if (!this.video.paused && !this.buffering) {
      this.userPaused = true;
      this.video.pause();
      this.clearStall();
      this.emit("paused");
      return;
    }
    this.manual = true;
    this.userPaused = false;
    this.failed = false;
    this.start(this.buffering);
  }

  async start(retry = false) {
    if (
      !this.eligible() || this.recovering ||
      (!retry && (this.starting || !this.video.paused))
    ) return;
    if (retry) this.generation++;
    this.starting = true;
    const generation = this.generation;
    try {
      if (!this.video.getAttribute("src")) {
        if (
          this.choice.tier === "quality" &&
          !qualityAllowed(this.choice, this.connection)
        ) {
          const sources = await this.sourcesReady;
          if (
            generation !== this.generation || !this.eligible() || this.userPaused ||
            (!this.manual && holdVideo(this.connection, this.motion.matches))
          ) return;
          const fallback = sources.find((source) => source.tier !== "quality");
          if (!fallback) return this.stop("slow-connection");
          this.choice = fallback;
        }
        this.video.width = this.choice.width;
        this.video.height = this.choice.height;
        this.video.src = this.choice.url;
        this.video.load();
        this.emit("loading");
      }
      this.watchStall();
      await this.video.play();
    } catch (error) {
      if (generation !== this.generation || error.name === "AbortError") return;
      if (error.name === "NotSupportedError") this.recover();
      else this.stop("autoplay-blocked");
    } finally {
      if (generation === this.generation) this.starting = false;
    }
  }

  reveal() {
    if (
      !this.video.getAttribute("src") ||
      !this.eligible() ||
      this.video.paused
    )
      return;
    this.setAttribute("has-frame", "");
    if (!this.firstFrame) {
      this.firstFrame = true;
      this.emit("first-frame");
    }
  }

  watchStall() {
    if (this.stallTimer || this.idleTimer || this.failed || this.userPaused || !this.eligible()) return;
    this.watchProgress();
    this.stallTimer = setTimeout(() => {
      this.stallTimer = null;
      if (this.video.readyState < 3 && !this.userPaused && this.eligible()) {
        this.buffering = true;
        this.syncControl();
        this.emit("buffering");
      } else this.clearStall();
    }, 4000);
  }

  watchProgress() {
    // Native progress events mean bytes are arriving, even before metadata or
    // playable ranges exist. Retry taps must not renew this idle deadline.
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (this.video.readyState < 3 && !this.userPaused && this.eligible())
        this.stop("slow-connection");
      else this.clearStall();
    }, 20000);
  }

  clearStall() {
    clearTimeout(this.stallTimer);
    clearTimeout(this.idleTimer);
    this.stallTimer = null;
    this.idleTimer = null;
    this.buffering = false;
    if (this.video) this.syncControl();
  }

  async recover() {
    if (!this.video.getAttribute("src") || this.failed || this.recovering) return;
    if (this.video.error?.code === 2) return this.stop("network-error");
    if (this.retries++ >= 1) return this.stop("unavailable");
    const failedCodec = this.choice.codec;
    this.recovering = true;
    this.unload();
    const generation = this.generation;
    const sources = await this.sourcesReady;
    if (!this.isConnected || generation !== this.generation) return;
    this.recovering = false;
    const next = sources.find(
      (source) =>
        source.codec !== failedCodec && source.tier !== "quality",
    );
    if (!next) return this.stop("unavailable");
    this.choice = next;
    this.emit("codec-fallback");
    this.update();
  }

  stop(reason) {
    this.failed = true;
    this.unload();
    this.syncControl();
    this.emit(reason);
  }

  suspend() {
    this.clearStall();
    this.video?.cancelVideoFrameCallback?.(this.frameCallback);
    if (!this.video?.getAttribute("src")) return;
    this.generation++;
    this.starting = false;
    this.video.pause();
    this.syncControl();
  }

  unload() {
    this.suspend();
    if (!this.video?.getAttribute("src")) return;
    this.resumeTime = this.video.currentTime;
    this.video.removeAttribute("src");
    this.video.load();
    this.removeAttribute("has-frame");
    this.syncControl();
  }

  syncControl() {
    const playing = !this.video.paused && !this.buffering;
    this.button?.setAttribute(
      "aria-label",
      playing ? this.dataset.pauseLabel : this.dataset.playLabel,
    );
    this.button?.setAttribute("data-playing", String(playing));
    this.button?.setAttribute(
      "data-manual-play",
      String(
        !playing && this.ready && (
          this.buffering || this.failed || this.userPaused ||
          (!this.manual && holdVideo(this.connection, this.motion.matches))
        ),
      ),
    );
  }

  emit(state) {
    this.dataset.state = state;
    this.dataset.rendition = this.choice?.name || "";
    performance.mark?.(`hero-video:${state}`);
    this.dispatchEvent(
      new CustomEvent("hero-video:state", {
        bubbles: true,
        detail: { state, rendition: this.choice?.name, mobile: this.mobile },
      }),
    );
  }
}

if (!customElements.get("hero-video"))
  customElements.define("hero-video", HeroVideo);
